import { useEffect, useState } from "react";
import { Outlet, Link, useLocation, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { AnimatePresence, LazyMotion, domAnimation, m, motion, useScroll, useSpring } from "framer-motion";
import { useAuth } from "../stores/auth";
import { toast } from "../stores/toast";
import { api } from "../lib/api";
import { timeAgo } from "../lib/format";
import {
  notificationTypeLabel,
  resolveNotificationPath,
  type NotificationItem,
} from "../lib/notifications";
import { getSocket } from "../lib/socket";
import { isActionablePendingPayment } from "../lib/pendingPayment";
import { Toasts } from "./ui";
import { AiChatWidget } from "./AiChatWidget";
import { SiteFooter } from "./social/SiteFooter";
import { SocialFab } from "./social/SocialFab";
import { PinSetupBanner } from "./PinSetupBanner";
import { SessionLockOverlay } from "./SessionLockOverlay";
import { useSessionIdle } from "../hooks/useSessionIdle";
import { needsPhoneVerification } from "../lib/phoneVerification";
import { AppHeader } from "./shell/AppHeader";
import { MobileBottomNav } from "./shell/MobileBottomNav";
import { DesktopSidebar } from "./shell/DesktopSidebar";
import { CommandPalette, useCommandPaletteShortcut } from "./shell/CommandPalette";
import { NavigationLoader } from "./shell/NavigationLoader";

export function Layout() {
  const { user, moduleEnabled, config } = useAuth();
  const phoneGate = needsPhoneVerification(user, config);
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [notifOpen, setNotifOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);

  useCommandPaletteShortcut(() => setPaletteOpen(true), true);

  const { scrollYProgress } = useScroll();
  const progress = useSpring(scrollYProgress, { stiffness: 200, damping: 40, restDelta: 0.001 });

  const { data: cart } = useQuery({
    queryKey: ["cart"],
    queryFn: () =>
      api<{ items: { quantity: number }[]; packItems: { quantity: number }[] }>("/cart"),
    enabled: !!user && !phoneGate && moduleEnabled("ecommerce"),
    staleTime: 10_000,
  });
  const { data: ordersData } = useQuery({
    queryKey: ["orders"],
    queryFn: () =>
      api<{
        items: { status: string; paymentDeadline: string | null; latestReceiptStatus: string | null }[];
      }>("/orders/mine"),
    enabled: !!user && !phoneGate && moduleEnabled("ecommerce"),
    staleTime: 30_000,
  });
  const { data: notifications } = useQuery({
    queryKey: ["notifications", "preview"],
    queryFn: () =>
      api<{ items: NotificationItem[]; unread: number }>("/notifications?limit=10"),
    enabled: !!user && !phoneGate,
    staleTime: 30_000,
  });

  useEffect(() => {
    if (!user) return;
    const socket = getSocket();
    const onNotification = (n: { title: string }) => {
      toast("info", n.title);
      void queryClient.invalidateQueries({ queryKey: ["notifications"] });
    };
    const onOrderPaid = () => {
      void queryClient.invalidateQueries({ queryKey: ["orders"] });
    };
    socket.on("notification", onNotification);
    socket.on("order:paid", onOrderPaid);
    return () => {
      socket.off("notification", onNotification);
      socket.off("order:paid", onOrderPaid);
    };
  }, [user, queryClient]);

  const cartCount =
    (cart?.items.reduce((sum, item) => sum + item.quantity, 0) ?? 0) +
    (cart?.packItems?.reduce((sum, item) => sum + item.quantity, 0) ?? 0);
  const pendingPaymentCount = ordersData?.items.filter(isActionablePendingPayment).length ?? 0;
  const unread = notifications?.unread ?? 0;
  const previewItems = (notifications?.items ?? []).slice(0, 10);

  function openNotification(item: NotificationItem) {
    if (!item.readAt) {
      void api("/notifications/read", { method: "POST", body: { ids: [item.id] } }).then(() =>
        queryClient.invalidateQueries({ queryKey: ["notifications"] }),
      );
    }
    const path = resolveNotificationPath(item, { collectionEnabled: moduleEnabled("collection") });
    setNotifOpen(false);
    if (path) navigate(path);
  }

  useSessionIdle();

  return (
    <div className="mx-auto min-h-screen max-w-lg bg-brand-dark lg:max-w-none">
      <Toasts />
      <NavigationLoader />

      {/* reading progress — desktop chrome only */}
      <motion.div
        aria-hidden
        style={{ scaleX: progress }}
        className="fixed inset-x-0 top-0 z-50 hidden h-0.5 origin-left bg-gradient-brand lg:block"
      />

      <div className="lg:flex lg:min-h-screen">
        <DesktopSidebar />

        <div className="bg-noise relative flex min-w-0 flex-1 flex-col bg-mesh">
          <AppHeader
            unread={unread}
            cartCount={cartCount}
            pendingPaymentCount={pendingPaymentCount}
            notifOpen={notifOpen}
            onToggleNotif={() => setNotifOpen((v) => !v)}
            onOpenSearch={() => setPaletteOpen(true)}
          />

          <AnimatePresence>
            {notifOpen && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 z-50"
                onClick={() => setNotifOpen(false)}
              >
                <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
                <motion.div
                  initial={{ opacity: 0, y: -10, scale: 0.97 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -10, scale: 0.97 }}
                  transition={{ duration: 0.2 }}
                  className="glass-panel-strong absolute right-2 top-14 flex max-h-[70vh] w-80 max-w-[calc(100vw-1rem)] flex-col overflow-hidden rounded-2xl shadow-2xl lg:right-6 lg:top-16"
                  onClick={(e) => e.stopPropagation()}
                >
                  <p className="shrink-0 px-3 py-2 font-display text-xs font-semibold uppercase tracking-widest text-white/50">
                    Notifications{unread > 0 ? ` · ${unread} unread` : ""}
                  </p>

                  <div className="min-h-0 flex-1 overflow-y-auto">
                    {previewItems.length ? (
                      <div className="divide-y divide-white/10">
                        {previewItems.map((n, i) => (
                          <motion.button
                            key={n.id}
                            type="button"
                            initial={{ opacity: 0, x: 10 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: i * 0.03 }}
                            onClick={() => openNotification(n)}
                            className={`block w-full px-3 py-2.5 text-left transition-colors hover:bg-white/5 ${
                              n.readAt ? "opacity-60" : "bg-white/[0.03]"
                            }`}
                          >
                            <p className="text-xs font-semibold text-white/90">{n.title}</p>
                            {(n.body || n.type) && (
                              <p className="mt-0.5 line-clamp-2 text-[11px] text-white/60">
                                {n.body ?? notificationTypeLabel(n.type)}
                              </p>
                            )}
                            <p className="mt-1 text-[10px] text-white/35">{timeAgo(n.createdAt)}</p>
                          </motion.button>
                        ))}
                      </div>
                    ) : (
                      <div className="flex flex-col items-center gap-2 px-3 py-8">
                        <img src="/art/mascot.svg" alt="" className="h-16 w-16 opacity-50" />
                        <p className="text-center text-xs text-white/40">Nothing here yet</p>
                      </div>
                    )}
                  </div>

                  <Link
                    to="/notifications"
                    onClick={() => setNotifOpen(false)}
                    className="shrink-0 border-t border-white/10 px-3 py-2.5 text-center text-xs font-semibold text-brand-pink transition-colors hover:bg-white/5"
                  >
                    View all
                  </Link>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>

          <main className="safe-bottom min-h-[70vh] flex-1">
            <PinSetupBanner />
            <div className="mx-auto w-full min-w-0 overflow-x-hidden lg:max-w-[1400px]">
              <LazyMotion features={domAnimation}>
                <m.div
                  key={location.pathname}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.28, ease: [0.16, 1, 0.3, 1] }}
                >
                  <Outlet />
                </m.div>
              </LazyMotion>
            </div>
            <SiteFooter />
          </main>
        </div>
      </div>

      {user && moduleEnabled("ai_chat_fe") && <AiChatWidget />}
      <SocialFab />
      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />
      <MobileBottomNav cartCount={cartCount} pendingPaymentCount={pendingPaymentCount} />
      <ScrollReset key={location.pathname} />
      <SessionLockOverlay />
    </div>
  );
}

function ScrollReset() {
  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, []);
  return null;
}
